export type BeatLineUpdateUIContext = {
  state: Record<string, any>;
  beatlineUi: Record<string, any>;
  beatlineLang?: string;
  beatlineUpdateActiveScorePanel?: (slot: number) => void;
  beatlineShouldUseCompactLayout?: (playerCount: number) => boolean;
  beatlineUpdateStatsVisibilityOffline?: (playerCount: number) => void;
  beatlineUpdateTimelineLabelOffsets?: (playerCount: number) => void;
  beatlineEnsureHeaderActiveFirst?: (playerCount: number) => void;
  beatlineRenderHeaderActiveFirst?: (opts: { playerCount: number; turnSlot: number; activeSlots: number[] }) => void;
  beatlineInitZoneFocusClicks?: () => void;
  beatlineCreateMiniYearHTML?: (year: unknown) => string;
  createCardHTML?: (card: Record<string, any>, revealed: boolean, opts?: Record<string, any>) => string;
};

function intValue(value: unknown, fallback: number): number {
  const parsed = parseInt(String(value ?? ''), 10);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function domPlayerCount(): number {
  try {
    const checked = document.querySelector<HTMLInputElement>('input[name="p_count"]:checked')?.value || '';
    const parsed = parseInt(checked, 10);
    return Number.isFinite(parsed) && parsed >= 1 && parsed <= 4 ? parsed : 0;
  } catch (_) {
    return 0;
  }
}

function forceSinglePlayerFromControlsOrMode(state: Record<string, any>): boolean {
  const dom = domPlayerCount();
  if (dom === 1) return true;
  return !!(state?.perfectOrder || state?.timeAttack || state?.survival || state?.speedrun);
}

function coercePlayerCount(state: Record<string, any>): number {
  const dom = domPlayerCount();
  if (dom === 1 || forceSinglePlayerFromControlsOrMode(state)) {
    try { state.playerCount = 1; state.turn = 1; } catch (_) {}
    return 1;
  }
  const stateCount = intValue(state.playerCount || dom || 1, 1);
  return Math.max(1, Math.min(4, stateCount));
}

function playerState(state: Record<string, any>, slot: number): Record<string, any> {
  const player = state[`p${slot}`];
  return player && typeof player === 'object' ? player : { score: 0, tokens: 0, timeline: [] };
}

function safeCall(fn: unknown, ...args: any[]): void {
  if (typeof fn !== 'function') return;
  try { fn(...args); } catch (_) {}
}

function activeSlots(playerCount: number): number[] {
  return Array.from({ length: playerCount }, (_, index) => index + 1);
}

function renderFullTimeline(zone: HTMLElement, timeline: Record<string, any>[], createCardHTML?: BeatLineUpdateUIContext['createCardHTML']): void {
  timeline.forEach((card) => {
    const html = typeof createCardHTML === 'function'
      ? createCardHTML(card, true)
      : `<div class="card revealed" data-id="${String(card.id || '')}" data-year="${String(card.year || '')}"><div class="card-artist">${String(card.title || '')}</div><div class="card-icon">🎵</div><div class="card-year">${String(card.year || '')}</div><div class="card-title">${String(card.title || '')}</div></div>`;
    zone.insertAdjacentHTML('beforeend', html);
  });
}

function renderCompactTimeline(zone: HTMLElement, timeline: Record<string, any>[], miniYearHTML?: BeatLineUpdateUIContext['beatlineCreateMiniYearHTML']): void {
  timeline.forEach((card) => {
    const html = typeof miniYearHTML === 'function'
      ? miniYearHTML(card.year)
      : `<div class="mini-year">${String(card.year || '')}</div>`;
    zone.insertAdjacentHTML('beforeend', html);
  });
}

export function updateUIFromTypeScript(skipTimelineRebuild = false, ctx: BeatLineUpdateUIContext): void {
  const { state, beatlineUi } = ctx;

  if (!state || !beatlineUi) return;
  try { (window as any).__beatlineTsLastState = state; } catch (_) {}

  // Perfect Order beheert de DOM zelf, exact zoals de originele runtime.
  if (state.perfectOrder) skipTimelineRebuild = true;

  const playerCount = coercePlayerCount(state);
  const turnSlot = playerCount === 1 ? 1 : intValue(state.turn || 1, 1);
  const compact = typeof ctx.beatlineShouldUseCompactLayout === 'function'
    ? !!ctx.beatlineShouldUseCompactLayout(playerCount)
    : false;

  const activeScorePanel = document.getElementById('active-score-panel');
  const isSinglePlayer = playerCount <= 1;
  const isSpeedrun = !!state.speedrun;
  const shouldHideActiveScorePanel = !!(state.perfectOrder || state.timeAttack || state.survival || (isSinglePlayer && !isSpeedrun));
  if (shouldHideActiveScorePanel) {
    if (activeScorePanel) {
      activeScorePanel.style.setProperty('display', 'none', 'important');
      activeScorePanel.setAttribute('hidden', 'hidden');
      activeScorePanel.setAttribute('aria-hidden', 'true');
    }
  } else {
    if (activeScorePanel) {
      activeScorePanel.removeAttribute('hidden');
      activeScorePanel.setAttribute('aria-hidden', 'false');
      activeScorePanel.style.setProperty('display', 'grid', 'important');
      activeScorePanel.style.setProperty('visibility', 'visible', 'important');
    }
    safeCall(ctx.beatlineUpdateActiveScorePanel, turnSlot);
    if (state.speedrun && activeScorePanel) {
      const countEl = document.getElementById('active-score-count');
      const neededEl = document.getElementById('active-score-needed');
      const target = intValue(state.speedrunTarget || state.winTarget || 10, 10);
      const p = playerState(state, turnSlot);
      const timelineCards = Array.isArray(p.timeline) ? p.timeline.length : 0;
      const score = intValue(p.score, 0);
      const cards = Math.max(0, score, timelineCards > 0 ? timelineCards - 1 : 0);
      const done = Math.min(target, Math.max(0, cards));
      if (countEl) countEl.textContent = `${done}/${target}`;
      if (neededEl) {
        const left = Math.max(0, target - done);
        activeScorePanel.classList.toggle('is-complete', left <= 0);
        neededEl.innerHTML = left <= 0
          ? ((String(ctx.beatlineLang || (window as any).beatlineLang || 'nl').toLowerCase() === 'en')
            ? '<span class="active-score-panel__needed-word">DONE</span>'
            : '<span class="active-score-panel__needed-word">KLAAR</span>')
          : ((String(ctx.beatlineLang || (window as any).beatlineLang || 'nl').toLowerCase() === 'en')
            ? `<span class="active-score-panel__needed-word">NEED</span><strong class="active-score-panel__needed-number">${left}</strong><span class="active-score-panel__needed-word">MORE</span>`
            : `<span class="active-score-panel__needed-word">NOG</span><strong class="active-score-panel__needed-number">${left}</strong><span class="active-score-panel__needed-word">NODIG</span>`);
      }
    }
  }

  if (!compact) {
    beatlineUi.focusSlot = turnSlot;
    beatlineUi.userSetFocus = false;
  } else {
    if (!beatlineUi.focusSlot || beatlineUi.focusSlot > playerCount) beatlineUi.focusSlot = turnSlot;
    if (turnSlot !== beatlineUi.lastTurn) {
      beatlineUi.focusSlot = turnSlot;
      beatlineUi.userSetFocus = false;
    }
  }
  beatlineUi.lastTurn = turnSlot;

  const battlefield = document.querySelector<HTMLElement>('.battlefield');
  if (battlefield) {
    battlefield.classList.toggle('multi-compact', !!compact);
    battlefield.classList.remove('pc-2', 'pc-3', 'pc-4');
    if (compact) battlefield.classList.add(`pc-${playerCount}`);
  }

  safeCall(ctx.beatlineUpdateStatsVisibilityOffline, playerCount);
  safeCall(ctx.beatlineUpdateTimelineLabelOffsets, playerCount);
  safeCall(ctx.beatlineEnsureHeaderActiveFirst, playerCount);
  safeCall(ctx.beatlineRenderHeaderActiveFirst, {
    playerCount,
    turnSlot,
    activeSlots: activeSlots(playerCount),
  });
  safeCall(ctx.beatlineInitZoneFocusClicks);

  // C27: make the TypeScript renderer authoritative for visible player zones.
  // After leaving a multiplayer game and starting solo without a hard reload,
  // legacy DOM could re-show old player timelines. Hide all inactive slots before
  // rendering the active set, and repeat at the end as a safety net.
  for (let slot = playerCount + 1; slot <= 4; slot += 1) {
    const extraZone = document.getElementById(`zone-p${slot}`) as HTMLElement | null;
    const extraTimeline = document.getElementById(`timeline-p${slot}`) as HTMLElement | null;
    const extraStats = document.getElementById(`stats-p${slot}`) as HTMLElement | null;
    if (extraTimeline && (!skipTimelineRebuild || playerCount === 1)) {
      extraTimeline.innerHTML = '';
      extraTimeline.classList.remove('timeline-compact', 'sortable-chosen', 'sortable-ghost', 'sortable-drag', 'has-pending');
    }
    if (extraZone) {
      extraZone.style.setProperty('display', 'none', 'important');
      extraZone.style.order = '';
      extraZone.classList.remove('zone-active', 'zone-focus', 'zone-compact', 'zone-dimmed', 'is-active', 'has-pending');
    }
    if (extraStats) {
      extraStats.classList.remove('active-turn', 'visible', 'active', 'is-active');
      (extraStats as HTMLElement).style.setProperty('display', 'none', 'important');
    }
  }

  for (let slot = 1; slot <= playerCount; slot += 1) {
    const player = playerState(state, slot);

    const scoreEl = document.getElementById(`score-p${slot}`);
    const tokenEl = document.getElementById(`tokens-p${slot}`);
    if (scoreEl) scoreEl.innerText = String(player.score ?? 0);
    if (tokenEl) tokenEl.innerText = String(player.tokens ?? 0);

    if (!skipTimelineRebuild) {
      const zone = document.getElementById(`timeline-p${slot}`);
      if (zone) {
        zone.innerHTML = '';
        const timeline = Array.isArray(player.timeline) ? player.timeline : [];
        timeline.sort((a: Record<string, any>, b: Record<string, any>) => (a.year || 0) - (b.year || 0));

        const isFocused = !compact ? turnSlot === slot : beatlineUi.focusSlot === slot;
        if (isFocused) {
          zone.classList.remove('timeline-compact');
          renderFullTimeline(zone, timeline, ctx.createCardHTML);
        } else {
          zone.classList.add('timeline-compact');
          renderCompactTimeline(zone, timeline, ctx.beatlineCreateMiniYearHTML);
        }
      }
    }

    const zoneContainer = document.getElementById(`zone-p${slot}`);
    const statsContainer = document.getElementById(`stats-p${slot}`);
    const isTurn = turnSlot === slot;
    const isFocus = !compact ? isTurn : beatlineUi.focusSlot === slot;

    if (zoneContainer) {
      zoneContainer.style.setProperty('display', 'flex', 'important');
      zoneContainer.style.setProperty('visibility', 'visible', 'important');
      zoneContainer.removeAttribute('hidden');
      zoneContainer.setAttribute('aria-hidden', 'false');
      zoneContainer.classList.toggle('zone-active', isTurn);
      zoneContainer.classList.toggle('zone-focus', isFocus);
      zoneContainer.classList.toggle('zone-compact', compact && !isFocus);
      // Originele plugin: beurtspeler bovenaan, overige strips eronder.
      zoneContainer.style.order = isTurn ? '1' : String(slot + 1);
    }

    if (statsContainer) {
      (statsContainer as HTMLElement).style.removeProperty('display');
      statsContainer.classList.toggle('active-turn', isTurn);
    }
  }

  for (let slot = playerCount + 1; slot <= 4; slot += 1) {
    const extraZone = document.getElementById(`zone-p${slot}`) as HTMLElement | null;
    const extraStats = document.getElementById(`stats-p${slot}`) as HTMLElement | null;
    if (extraZone) extraZone.style.setProperty('display', 'none', 'important');
    if (extraStats) extraStats.style.setProperty('display', 'none', 'important');
  }

  if (!skipTimelineRebuild) {
    const turnDisplay = document.getElementById('turn-display');
    const lang = String(ctx.beatlineLang || (window as any).beatlineLang || 'nl').toLowerCase();
    if (turnDisplay) {
      turnDisplay.innerText = (lang === 'en' ? 'PLAYER ' : 'SPELER ') + turnSlot;
      turnDisplay.style.color = `var(--accent-p${turnSlot})`;
    }
  }
}
